<template>
  <div id="map" class="w-full h-[98vh]"></div>
  <div
    class="absolute right-2.5 top-14 z-10 bg-white border-black/25 rounded-sm flex flex-col w-9 px-4 py-1 items-center"
  >
    <button
      type="button"
      class="p-1 rounded-sm inline-flex items-center me-2"
      @click="openHistory"
    >
      <IconHistory />
    </button>
  </div>

  <div class="absolute bottom-0 left-72 z-10">
    <PaginationMapsComponent
      v-model:length="length"
      v-model:size="limit"
      v-model:page="page"
      @onSelectChange="changePage"
    />
  </div>

  <div id="loading-overlay" v-if="sgnkso_features.loading">
    <LoadingLoad id="loading" class="" size="w-14 h-14" />
  </div>

  <HistoryArea v-show="showHistory" @gotoarea="gotoarea" :selected="selected" />
  <NewAreaGeofence :id="1" />
</template>

<style>
#map {
  z-index: 1;
}
.leaflet-popup-close-button {
  margin-top: 3px;
  margin-right: 3px;
  margin-bottom: 3px;
}
#loading-overlay {
  position: fixed;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.016); /* Semi-transparent white */
  z-index: 10; /* Ensure it's on top */
  backdrop-filter: blur(1px); /* Apply the blur effect */
}
#loading {
  position: absolute;
  top: 45%;
  left: 43%;
  transform: translate(-50%, -50%);
}
</style>

<script>
import L from "leaflet";
import { onMounted, ref } from "vue";
import HistoryArea from "./HistoryArea.vue";
import IconHistory from "@/components/icons/IconHistory.vue";
import {
  useAreaGeofenceStore,
  useModalStore,
  usesgnksoFeaturesStore,
} from "@/store";
import { storeToRefs } from "pinia";
import NewAreaGeofence from "@/components/NewAreaGeofence.vue";
import PaginationMapsComponent from "@/components/PaginationMapsComponent.vue";
import LoadingLoad from "@/components/LoadingLoad.vue";

export default {
  components: {
    NewAreaGeofence,
    HistoryArea,
    IconHistory,
    PaginationMapsComponent,
    LoadingLoad,
  },
  setup() {
    const modal = useModalStore();
    const areaStore = useAreaGeofenceStore();
    const { geofence } = storeToRefs(areaStore);
    const { showHistory, showKuota, showRequestDownload } =
      storeToRefs(areaStore);
    const sgnksoFeaturesStore = usesgnksoFeaturesStore();
    const { sgnkso_features } = storeToRefs(sgnksoFeaturesStore);

    const map = ref();
    const center = ref([-1.476796, 121.001893]);
    const zoom = ref(5);

    const limit = ref(500);
    const offset = ref(0);
    const length = ref(0);
    const page = ref(1);

    const shapeLayer = ref();

    const selected = ref(null);

    var drawnItems = ref();

    const createMap = () => {
      map.value = L.map("map", { zoomAnimation: false }).setView(
        center.value,
        zoom.value
      );
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
        {
          minZoom: 5,
          attribution: "Citra Satelite © 2025",
        }
      ).addTo(map.value);

      console.log("Bounds:", map.value.getBounds().toBBoxString());

      L.control.scale().addTo(map.value);
      L.control.ruler().addTo(map.value);

      var drawnMarker = L.Icon.extend({
        options: {
          shadowUrl: null,
          iconAnchor: new L.Point(12, 12),
          iconSize: new L.Point(24, 24),
          iconUrl: require("../../assets/marker.png"),
        },
      });

      drawnItems.value = new L.FeatureGroup();
      map.value.addLayer(drawnItems.value);
      var drawControl = new L.Control.Draw({
        draw: {
          marker: {
            icon: new drawnMarker(),
          },
        },
        edit: {
          featureGroup: drawnItems.value,
        },
      });
      map.value.addControl(drawControl);

      //create area
      map.value.on("draw:created", function (e) {
        var type = e.layerType,
          layer = e.layer;

        drawnItems.value.addLayer(layer);

        var shape = layer.toGeoJSON();
        console.log(JSON.stringify(shape));

        if (type == "circle" || type == "circlemarker") {
          const radius = layer.getRadius();
          shape.properties = { type: type, radius: radius };
        }
        if (type == "marker") {
          shape.properties = { type: type };
        }

        areaStore.drawArea = shape;
        modal.showModal(1);
      });

      ///edit area
      map.value.on("draw:edited", function (e) {
        var layers = e.layers;
        layers.eachLayer(async function (layer) {
          if (layer instanceof L.Marker) {
            areaStore.geoEdited = layer.toGeoJSON();
            areaStore.areaId = areaStore.geoEdited.properties.id;
            areaStore.dataById.area = areaStore.geoEdited.properties.area;
            areaStore.dataById.kategori_area =
              areaStore.geoEdited.properties.kategori_area;
          } else if (
            layer instanceof L.Circle ||
            layer instanceof L.CircleMarker
          ) {
            areaStore.geoEdited = layer.toGeoJSON();
            areaStore.areaId = areaStore.geoEdited.properties.id;
            const radius = layer.getRadius();
            areaStore.geoEdited.properties.radius = radius;
            areaStore.dataById.area = areaStore.geoEdited.properties.area;
            areaStore.dataById.kategori_area =
              areaStore.geoEdited.properties.kategori_area;
          } else {
            areaStore.geoEdited = layer.toGeoJSON();
            await areaStore.areaById(areaStore.geoEdited.properties.id);
          }
          modal.showModal(2);
        });
      });
    };

    const onEachFeature = async (feature, layer) => {
      if (feature.properties) {
        const div = document.createElement("div");

        const info = document.createElement("div");

        const table = document.createElement("table");
        info.appendChild(table);

        for (var key in feature.properties) {
          const row = document.createElement("tr");
          table.appendChild(row);

          const cell1 = document.createElement("td");
          cell1.setAttribute("class", "align-text-top");
          const cell2 = document.createElement("td");
          cell2.setAttribute("class", "px-2 align-text-top");
          const cell3 = document.createElement("td");

          const textNode1 = document.createTextNode(key);
          const textNode2 = document.createTextNode(":");
          const textNode3 = document.createTextNode(feature.properties[key]);
          cell1.appendChild(textNode1);
          cell2.appendChild(textNode2);
          cell3.appendChild(textNode3);
          row.appendChild(cell1);
          row.appendChild(cell2);
          row.appendChild(cell3);
        }

        div.appendChild(info);
        layer.bindPopup(div, {
          maxHeight: 430,
        });
      }
    };

    const showGeofence = async () => {
      await sgnksoFeaturesStore.getAllSgnksofeatures({ limit: limit.value });
      length.value = sgnkso_features.value.count;

      const geojson = sgnkso_features.value.rows;

      shapeLayer.value = new L.Shapefile(geojson, {
        onEachFeature: onEachFeature,
      });
      map.value.fitBounds(shapeLayer.value.getBounds());
      shapeLayer.value.addTo(map.value);
    };

    const changePage = async (value) => {
      page.value = value;
      offset.value = (page.value - 1) * limit.value;

      await sgnksoFeaturesStore.getAllSgnksofeatures({
        limit: limit.value,
        offset: offset.value,
      });
      length.value = sgnkso_features.value.count;

      const geojson = sgnkso_features.value.rows;
      map.value.removeLayer(shapeLayer.value);

      shapeLayer.value = new L.Shapefile(geojson, {
        onEachFeature: onEachFeature,
      });

      map.value.fitBounds(shapeLayer.value.getBounds());
      shapeLayer.value.addTo(map.value);
    };

    onMounted(async () => {
      showHistory.value = false;
      await createMap();
      await showGeofence();
    });

    const openHistory = () => {
      showHistory.value = !showHistory.value;
    };

    const openInfoKuota = () => {
      showHistory.value = false;
      showKuota.value = !showKuota.value;
    };

    const featuresLayer = ref(null);
    const gotoarea = async (val) => {
      selected.value = val;

      let obj = sgnkso_features.value.rows.find((o) => o.feature_id === val);
      console.log(obj);
      if (obj) {
        shapeLayer.value.eachLayer(function (layer) {
          if (layer.feature.feature_id.includes(val)) {
            map.value.fitBounds(layer.getBounds());
            layer.openPopup();
          }
        });
      } else {
        await sgnksoFeaturesStore.getByFeatureid(val);

        if (map.value.hasLayer(featuresLayer.value)) {
          await map.value.removeLayer(featuresLayer.value);
        }

        featuresLayer.value = L.geoJson(sgnksoFeaturesStore.featurebyid, {
          onEachFeature: onEachFeature,
        }).addTo(map.value);

        featuresLayer.value.eachLayer(function (layer) {
          if (layer.feature.feature_id == val) {
            map.value.fitBounds(layer.getBounds());
            layer.openPopup();
          }
        });
      }
    };

    return {
      openHistory,
      openInfoKuota,
      showHistory,
      geofence,
      changePage,
      limit,
      page,
      length,
      showKuota,
      showRequestDownload,
      showGeofence,
      gotoarea,
      selected,
      sgnkso_features,
    };
  },
};
</script>
